import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import useAuth from '../../hooks/useAuth';
import './CheckoutStyles.css';

const AddressForm = ({ onAddressSelect, selectedAddress, onContinue }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    isDefault: false
  });
  const [errors, setErrors] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchAddresses();
  }, [user, navigate]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/addresses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAddresses(data);

        if (!selectedAddress) {
          const defaultAddress = data.find((addr) => addr.isDefault);
          if (defaultAddress) {
            onAddressSelect(defaultAddress);
          } else if (data.length > 0) {
            onAddressSelect(data[0]);
          }
        }
      } else {
        console.error('Failed to fetch addresses');
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^\d]/g, '');
    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    setFormData({ ...formData, phone: value });

    if (errors.phone) {
      setErrors({ ...errors, phone: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newAddress = await response.json();
        setAddresses([...addresses, newAddress]);
        onAddressSelect(newAddress);
        setShowNewAddressForm(false);
        setFormData({
          fullName: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          pincode: '',
          phone: '',
          isDefault: false
        });
      } else {
        const errorData = await response.json();
        alert('Failed to add address: ' + errorData.message);
      }
    } catch (error) {
      console.error('Error adding address:', error);
      alert('An error occurred while adding your address. Please try again.');
    }
  };

  const handleAddressSelect = (address) => {
    onAddressSelect(address);
  };

  const handleContinue = () => {
    if (selectedAddress) {
      onContinue();
    } else {
      alert('Please select a shipping address to continue.');
    }
  };

  if (loading) {
    return <div className="loading">Loading addresses...</div>;
  }

  return (
    <div className="address-form-container">
      <h2>Shipping Address</h2>

      {addresses.length > 0 && (
        <div className="saved-addresses">
          <h3>Saved Addresses</h3>
          <div className="address-list">
            {addresses.map((address) => (
              <div
                key={address._id}
                className={`address-card ${selectedAddress && selectedAddress._id === address._id ? 'selected' : ''}`}
                onClick={() => handleAddressSelect(address)}
              >
                {address.isDefault && <span className="default-badge">Default</span>}
                <h4>{address.fullName}</h4>
                <p>{address.addressLine1}</p>
                {address.addressLine2 && <p>{address.addressLine2}</p>}
                <p>
                  {address.city}, {address.state} - {address.pincode}
                </p>
                <p>Phone: {address.phone}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!showNewAddressForm ? (
        <button
          className="add-address-btn"
          onClick={() => setShowNewAddressForm(true)}
        >
          + Add New Address
        </button>
      ) : (
        <div className="new-address-form">
          <h3>Add New Address</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="fullName">Full Name*</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={errors.fullName ? 'error' : ''}
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="addressLine1">Address Line 1*</label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                className={errors.addressLine1 ? 'error' : ''}
              />
              {errors.addressLine1 && <span className="error-message">{errors.addressLine1}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City*</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'error' : ''}
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="state">State*</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={errors.state ? 'error' : ''}
                />
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pincode">Pincode*</label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  maxLength="6"
                  className={errors.pincode ? 'error' : ''}
                />
                {errors.pincode && <span className="error-message">{errors.pincode}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number*</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  maxLength="10"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>
            </div>

            <div className="default-checkbox-row">
              <input
                type="checkbox"
                id="isDefault"
                name="isDefault"
                checked={formData.isDefault}
                onChange={handleInputChange}
              />
              <label className="default-checkbox-label" htmlFor="isDefault">
                Set as default address
              </label>
            </div>

            <div className="form-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowNewAddressForm(false)}
              >
                Cancel
              </button>
              <button type="submit" className="save-btn">Save Address</button>
            </div>
          </form>
        </div>
      )}

      <div className="button-group">
        <button
          className="continue-button"
          onClick={handleContinue}
          disabled={!selectedAddress}
        >
          Continue to Payment
        </button>
      </div>
    </div>
  );
};

export default AddressForm;
